import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CircleAlert,
  LoaderCircle,
  Repeat2,
  ShieldCheck,
  WalletCards,
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatUsd } from "../utils/money";
import { walletApi } from "../api/wallet";
import { useCopy } from "../i18n/copy";
import { useSession } from "../store/session";

export function TeamTransferPage() {
  const { user, isPreview, setSession } = useSession();
  const { tr } = useCopy();
  const [amount, setAmount] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const numericAmount = Number(amount);
  const hasValidIncrement =
    Number.isInteger(numericAmount) && numericAmount >= 50 && numericAmount % 50 === 0;
  const valid =
    Number.isFinite(numericAmount) &&
    hasValidIncrement &&
    numericAmount <= user.teamBalance;
  const transfer = useMutation({
    mutationFn: async () => {
      await walletApi.transferTeamBalance(numericAmount);
      return walletApi.getUser();
    },
    onSuccess: (raw) => {
      setSession({
        ...user,
        balance: Number(raw.balance ?? user.balance + numericAmount),
        teamBalance: Number(
          raw.team_balance ?? user.teamBalance - numericAmount,
        ),
        score: Number(raw.score ?? user.score),
      });
      setAmount("");
      setReviewing(false);
    },
  });

  if (isPreview)
    return (
      <main className="page">
        <Link className="back-link" to="/account">
          <ArrowLeft /> {tr("Volver a mi cuenta")}
        </Link>
        <div className="empty-state">
          <Repeat2 />
          <h1>{tr("Iniciar sesión")}</h1>
          <Link className="button primary" to="/auth/login">
            {tr("Iniciar sesión")} <ArrowRight />
          </Link>
        </div>
      </main>
    );

  return (
    <main className="page team-transfer-page">
      <Link className="back-link" to="/account">
        <ArrowLeft /> {tr("Volver a mi cuenta")}
      </Link>
      <header className="page-title-row">
        <div>
          <p className="eyebrow">{tr("Billetera")}</p>
          <h1>{tr("Transferir saldo del equipo")}</h1>
        </div>
      </header>
      <section className="transfer-balances">
        <div>
          <span>
            <WalletCards />
          </span>
          <small>{tr("Saldo personal")}</small>
          <strong>{formatUsd(user.balance)}</strong>
        </div>
        <i>
          <ArrowRight />
        </i>
        <div>
          <span>
            <Repeat2 />
          </span>
          <small>{tr("Saldo del equipo")}</small>
          <strong>{formatUsd(user.teamBalance)}</strong>
        </div>
      </section>
      <section className="wallet-form-card transfer-form">
        <label className="field-label" htmlFor="team-transfer-amount">
          {tr("Monto")}
        </label>
        <div className="money-input">
          <span>$</span>
          <input
            id="team-transfer-amount"
            type="number"
            inputMode="decimal"
            min="50"
            step="50"
            max={user.teamBalance}
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value);
              setReviewing(false);
            }}
            placeholder="0.00"
          />
        </div>
        {numericAmount > user.teamBalance && (
          <div className="form-feedback error">
            <CircleAlert /> {tr("El monto supera el saldo disponible.")}
          </div>
        )}
        {numericAmount > 0 && !hasValidIncrement && (
          <div className="form-feedback error">
            <CircleAlert /> {tr("El monto debe ser múltiplo de $50.")}
          </div>
        )}
        {transfer.isSuccess && (
          <div className="form-feedback success">
            <BadgeCheck /> {tr("Transferencia completada.")}
          </div>
        )}
        {transfer.isError && (
          <div className="form-feedback error">
            <CircleAlert /> {transfer.error.message}
          </div>
        )}
        {!reviewing ? (
          <button
            className="button primary full"
            disabled={!valid}
            onClick={() => setReviewing(true)}
          >
            {tr("Continuar")} <ArrowRight />
          </button>
        ) : (
          <div className="confirm-panel">
            <p className="eyebrow">{tr("Confirmar transferencia")}</p>
            <div>
              <span>{tr("Monto")}</span>
              <strong>{formatUsd(numericAmount)}</strong>
            </div>
            <div className="confirm-actions">
              <button
                className="button secondary"
                onClick={() => setReviewing(false)}
              >
                {tr("Cancelar")}
              </button>
              <button
                className="button primary"
                disabled={transfer.isPending}
                onClick={() => transfer.mutate()}
              >
                {transfer.isPending ? (
                  <LoaderCircle className="spin" />
                ) : (
                  <ShieldCheck />
                )}{" "}
                {tr("Confirmar")}
              </button>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
